import $ from 'jquery'
import _ from 'lodash'
import globals from 'scoreboard/components/globals'
import BaseRouter from '../components/base-router'
import Overview from './display/dashboards-overview'
import DashboardsEdit from './edit/dashboards-edit'
import Nav from './dashboards-nav'
import appDisplay from 'scoreboard/components/app-display'
import Dashboard from './dashboard'
import EmbeddedDashboard from './embedded-dashboard'
import viewEditTabs from 'scoreboard/components/view-edit-tabs.hbs'
import DashboardsFolderOverview from './dashboards-folder-overview'
import FolderForm from 'scoreboard/components/folder-form'
import EmptyState from 'scoreboard/components/empty-state'
import sections from 'scoreboard/components/sections'
import metadata from 'scoreboard/components/metadata'
import config from 'scoreboard/components/config'
import i18n from 'scoreboard/components/i18n'
import persistedViewState from 'scoreboard/components/persisted-view-state'
import getCreateFolderFunction from 'scoreboard/components/get-create-folder-function'
import exportState from 'scoreboard/components/export/export-state'
import errorHandler from 'scoreboard/components/error-handler'

var DashboardsRouter = BaseRouter.extend({
	Nav: Nav,
	Model: Dashboard,
	section: 'dashboards',

	OverviewView: Overview,
	EditView: DashboardsEdit,
	FolderOverview: DashboardsFolderOverview,
	EmbeddedModel: EmbeddedDashboard,

	initialize: function () {
		BaseRouter.prototype.initialize.apply(this, arguments)
		var urlSection = sections.toUrl(this.section)
		this.route(`${urlSection}/:id(/)`, 'overview')
		this.route(`${urlSection}/:id(/)?state=:state`, 'overview')
		this.route(`${urlSection}/:id/export(/)`, 'export')
		this.route(`${urlSection}/:id/shared/:token(/)`, 'embeddedOverview')
		this.route(`${urlSection}/:id/edit(/)`, 'edit')
		this.route(`${urlSection}/:relativeId/new(/)`, 'create')
		this.route(`${urlSection}/:id/new-folder(/)`, 'createFolder')
		this.route(`organizations/:orgId/${urlSection}/new(/)`, 'createFirst')
		this.route(`organizations/:orgId/${urlSection}/new-folder(/)`, 'createFirstFolder')
	},

	overview: function(id, stateString) {
		if (this.handleNoCalendars({ heading: i18n.get('dashboards.dashboards')})) {
			return
		}

		let state = persistedViewState(stateString)
		console.log('state', state)

		const modelPromise = this.loadWithPost(id, overviewFetchOptions(state))

		appDisplay.display(modelPromise, (model) => {
			let view = this.createOverview(model, { state })

			this.setBrieferView(view)

			return view
		})
		this.navForModel({ id }, modelPromise)
	},

	loadWithPost (id, opts) {
		// Adapted from BaseRouter.load(), this loads a Dashboard model using
		// a POST request instead of a GET. Necessary when sending UI state
		// (like in exports and briefings), which could be too large to fit
		// in a GET query string. See #54652
		let deferred = new $.Deferred()
		this.currentFetch = $.ajax({
			method: 'post',
			url: `api/${sections.toUrl(this.section)}/${id}/get-with-data`,
			data: JSON.stringify(opts),
			success: (resp, status, xhr) => {
				let model = new this.Model(resp)
				this.applyModel(model)

				deferred.resolve(model, resp)
				delete this.currentFetch
			},
			error: (xhr, status, error) => {
				errorHandler(xhr)

				deferred.reject(xhr)
				delete this.currentFetch
			}
		})

		return deferred.promise()
	},

	export: function(id) {
		this.overview(id, exportState.fromQueryString)
	},

	embeddedOverview: function(id, token) {
		this.Model = this.EmbeddedModel
		if (this.handleNoCalendars({ heading: i18n.get('dashboards.dashboards')})) {
			return
		}
		appDisplay.display(this.load({id: id, token: token}), (model) => {
			return this.createEmbeddedOverview(model, token)
		})
	},

	edit: function(id) {
		if (this.handleNoCalendars({ heading: i18n.get('dashboards.dashboards')})) {
			return
		}
		const modelPromise = this.loadWithPost(id, {
			calendarPeriodId: globals.periodId,
			widgets: true,
			templateInfo: true,
			grandchildren: false,
			subscriptions: true
		})
		appDisplay.display(modelPromise, model => {
			if (!model.isEditable()) {
				let body = this._notEditableBody(model)
				return new EmptyState({ body, model })
			}
			if (model.get('type') === 'folder') {
				return new FolderForm({ model: model, tabs: this.EditView.prototype.tabsTemplate })
			} else {
				var editView = new this.EditView({model})
				return editView
			}
		})
		this.navForModel({ id, edit: true }, modelPromise)
	},

	create: function (relativeId, orgId) {
		if (this.handleNoCalendars({ heading: i18n.get('dashboards.dashboards')})) {
			return
		}

		$.when(this._resolveOrg(relativeId, orgId)).done(() => {
			let oid = orgId || globals.orgId
			var dash = new this.Model({
				relativeId,
				organizationId: oid,
				type: 'dashboard',
				icon: metadata[this.section].sectionIcon,
				section: this.section,
				background: 'gray'
			})

			// #28133 Strategy maps option set in app admin
			// Display the strategy maps with layout options if allowed in App Admin
			// #15254 get information for layout options
			let layouts =
				this.section === 'dashboards' ||
				(this.section === 'strategyMaps' && config.displayStrategyMaps)
			if (layouts) {
				const navPromise = this.nav({ id: relativeId, orgId: oid, toggleEditViewState: true })

				var xhr = $.get({
					url: 'api/' + sections.toUrl(this.section) + '/org-data/' + oid + '?calendarPeriodId=' + globals.periodId
				})
				appDisplay.display(xhr, (results) => {
					var edit = new this.EditView({model: dash, orgData: results, layouts: layouts})
					navPromise.then(() => {
						this.addTransient(edit, relativeId, dash)
					})
					return edit
				})
			} else {
				var edit = new this.EditView({model: dash})
				this.navForNewModel({
					relativeId,
					orgId,
					form: edit,
					model:dash
				})
			}
		})
	},

	createFolder: getCreateFolderFunction(true, { header: 'dashboards.dashboards' }),

	createFirst: function (orgId) {
		this.create(null, orgId)
	},

	createFirstFolder: function(orgId) {
		this.createFolder(null, orgId)
	},

	createEmbeddedOverview: function (model, token) {
		globals.periodId = model.get('periodId')
		return this.createOverview(model, {
			token,
			state: persistedViewState(model.get('uiState'))
		})
	},

	/**
	 * @param token (optional) The embedded token holding the dashboardId and databaseId.
	 * This must be provided when non-authenticated API requests for dashboard widgets
	 * are made (i.e. for image widgets)
	 */
	createOverview: function (model, opt = {}) {
		var View = model.get('type') == 'folder' ? this.FolderOverview : this.OverviewView
		return new View({
			model: model,
			tabs: viewEditTabs,
			overviewTabCode: 'common.view',
			embeddedToken: opt.token,
			state: opt.state
		})
	}
})

function overviewFetchOptions (state) {
	let zoomToIdsByChartId
	if (state?.widgets) {
		// Each widget state has a chartDesignerId and zoomToIds, and we need
		// to pull that out to a single map of chartDesignerId to zoomToIds
		zoomToIdsByChartId = _(state.widgets)
			.toPairs()
			.map(([widgetId, widgetState]) => {
				return [widgetState?.chartDesignerId, widgetState?.zoomToIds]
			})
			.filter(p => !!p[1])
			.fromPairs()
			.value()
	}

	return {
		calendarPeriodId: globals.periodId,
		widgets: true,
		grandchildren: true,
		subscriptions: true,
		// If there were any active dataset filters/drilldowns saved in the
		// UI state, send them in the initial dashboard request (important
		// for loading proper chart data and dataset tree items)
		liveFilters: state?.liveFilters,
		drilldownIdsByFieldId: state?.drilldownIdsByFieldId,
		zoomToIdsByChartId
	}
}

export default DashboardsRouter
export { overviewFetchOptions }

DashboardsRouter.__filename__ = 'dashboards/dashboards-router.js'
